/**
 * Sidebar Utilities
 *
 * Shared utility functions used by both the main sidebar module
 * and the flyout components.
 */

import { LARGE_SCREEN_BREAKPOINT } from "./config";
import type { SidebarMode } from "./types";

/**
 * Checks if the sidebar is currently in mini mode on a large screen.
 *
 * Returns true only when:
 * 1. The sidebar element exists
 * 2. The viewport is at least 1024px wide (lg breakpoint)
 * 3. The sidebar has data-sidebar-state="mini"
 *
 * Returns:
 *   boolean: True if in mini mode, false otherwise.
 */
export function checkMiniMode(): boolean {
  const sidebar = document.getElementById("sidebar");
  if (!sidebar) return false;

  // Check if we're on a large screen (lg breakpoint = 1024px)
  const isLargeScreen = window.matchMedia(LARGE_SCREEN_BREAKPOINT).matches;
  if (!isLargeScreen) return false;

  return sidebar.getAttribute("data-sidebar-state") === "mini";
}

/**
 * Checks if the device supports touch input.
 *
 * Used to differentiate between hover (desktop) and tap (mobile) interactions.
 *
 * Returns:
 *   boolean: True if touch is supported, false otherwise.
 */
export function isTouchDevice(): boolean {
  return "ontouchstart" in window || navigator.maxTouchPoints > 0;
}

/**
 * Gets the current sidebar mode from the DOM.
 *
 * Args:
 *   sidebar: The sidebar HTMLElement, or null.
 *
 * Returns:
 *   SidebarMode: "expanded" or "mini", defaults to "expanded" if element not found.
 */
export function getSidebarMode(sidebar: HTMLElement | null): SidebarMode {
  if (!sidebar) return "expanded";
  const state = sidebar.getAttribute("data-sidebar-state");
  return state === "mini" ? "mini" : "expanded";
}

/**
 * Sets the sidebar mode in the DOM.
 *
 * Updates both the sidebar element's data-sidebar-state attribute
 * and the document root's dataset for CSS selectors.
 *
 * Args:
 *   sidebar: The sidebar HTMLElement.
 *   mode: The mode to set ("expanded" or "mini").
 */
export function setSidebarMode(sidebar: HTMLElement, mode: SidebarMode): void {
  sidebar.setAttribute("data-sidebar-state", mode);
  document.documentElement.dataset.sidebarState = mode;
}

/**
 * Checks if the sidebar is on a large screen (lg breakpoint).
 *
 * Returns:
 *   boolean: True if viewport width >= 1024px.
 */
export function isLargeScreen(): boolean {
  return window.matchMedia(LARGE_SCREEN_BREAKPOINT).matches;
}

/**
 * Gets an element by ID with type narrowing.
 *
 * Args:
 *   id: The element ID to find.
 *
 * Returns:
 *   HTMLElement | null: The element or null if not found.
 */
export function getElementById(id: string): HTMLElement | null {
  return document.getElementById(id);
}
